import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type AnalysisTarget,
  getAnalysisTargetKey,
  parseAnalysisTarget,
  parseAnalysisTargets,
} from "@/lib/ai/analysis-target";

type UseAnalysisTargetSelectionStateResult = {
  analysisTargets: AnalysisTarget[];
  pageAnalysisTarget: AnalysisTarget | null;
  manualAnalysisTargets: AnalysisTarget[];
  setAnalysisTargets: (targets: AnalysisTarget[]) => void;
  addAnalysisTarget: (target: AnalysisTarget) => void;
  removeAnalysisTarget: (target: AnalysisTarget) => void;
  removeLastAnalysisTarget: () => void;
  clearAnalysisTargets: () => void;
  setPageAnalysisTarget: (target: AnalysisTarget | null) => void;
};

type UseAnalysisTargetSelectionStateOptions = {
  storageKey?: string;
};

const PERSISTED_SELECTION_STATE_VERSION = 3 as const;

type PersistedSelectionState = {
  version: typeof PERSISTED_SELECTION_STATE_VERSION;
  pageAnalysisTarget: AnalysisTarget | null;
  manualAnalysisTargets: AnalysisTarget[];
};

type SelectionState = {
  storageKey?: string;
  pageAnalysisTarget: AnalysisTarget | null;
  manualAnalysisTargets: AnalysisTarget[];
};

function readPersistedSelectionState(
  storageKey?: string,
): Pick<
  UseAnalysisTargetSelectionStateResult,
  "pageAnalysisTarget" | "manualAnalysisTargets"
> {
  if (!storageKey || typeof window === "undefined") {
    return {
      pageAnalysisTarget: null,
      manualAnalysisTargets: [],
    };
  }

  try {
    const rawState = window.sessionStorage.getItem(storageKey);
    if (!rawState) {
      return {
        pageAnalysisTarget: null,
        manualAnalysisTargets: [],
      };
    }
    const parsed = JSON.parse(rawState) as PersistedSelectionState;
    if (parsed.version !== PERSISTED_SELECTION_STATE_VERSION) {
      return {
        pageAnalysisTarget: null,
        manualAnalysisTargets: [],
      };
    }

    return {
      pageAnalysisTarget: parseAnalysisTarget(parsed.pageAnalysisTarget),
      manualAnalysisTargets: parseAnalysisTargets(parsed.manualAnalysisTargets),
    };
  } catch {
    return {
      pageAnalysisTarget: null,
      manualAnalysisTargets: [],
    };
  }
}

function createSelectionState(storageKey?: string): SelectionState {
  return {
    storageKey,
    ...readPersistedSelectionState(storageKey),
  };
}

/**
 * storageKey が切り替わっても、effect 経由の再同期を待たずに対応する保存状態を
 * その render で使う。これで「props -> effect -> setState」の1ターン遅れを避ける。
 */
function resolveSelectionState(
  state: SelectionState,
  storageKey?: string,
): SelectionState {
  if (state.storageKey === storageKey) {
    return state;
  }
  return createSelectionState(storageKey);
}

/**
 * AI チャットの分析対象を「page chip」と「ユーザー追加」に分離して管理する。
 *
 * `pageAnalysisTarget` は「現在の表示ページを AI の分析対象に含める」と明示されている
 * page chip を表す。chip を外したら page target 自体を保持せず、
 * `analysisTargets` では `page + manual` の表示順だけを制御する。
 *
 * `storageKey` が指定されると sessionStorage に自動保存し、route 再マウント後も
 * ユーザー手動コンテキストと page コンテキストを復元する。
 */
export function useAnalysisTargetSelectionState(
  options: UseAnalysisTargetSelectionStateOptions = {},
): UseAnalysisTargetSelectionStateResult {
  const { storageKey } = options;
  const [selectionState, setSelectionState] = useState<SelectionState>(() =>
    createSelectionState(storageKey),
  );
  const { pageAnalysisTarget, manualAnalysisTargets } = useMemo(
    () => resolveSelectionState(selectionState, storageKey),
    [selectionState, storageKey],
  );

  const analysisTargets = useMemo(() => {
    if (!pageAnalysisTarget) {
      return manualAnalysisTargets;
    }
    const pageKey = getAnalysisTargetKey(pageAnalysisTarget);
    return [
      pageAnalysisTarget,
      ...manualAnalysisTargets.filter(
        (target) => getAnalysisTargetKey(target) !== pageKey,
      ),
    ];
  }, [manualAnalysisTargets, pageAnalysisTarget]);

  const setPageAnalysisTarget = useCallback(
    (target: AnalysisTarget | null) => {
      const normalizedTarget = parseAnalysisTarget(target);
      setSelectionState((previousState) => ({
        ...resolveSelectionState(previousState, storageKey),
        pageAnalysisTarget: normalizedTarget,
      }));
    },
    [storageKey],
  );

  const setAnalysisTargets = useCallback(
    (targets: AnalysisTarget[]) => {
      const normalizedTargets = parseAnalysisTargets(targets);
      setSelectionState((previousState) => ({
        ...resolveSelectionState(previousState, storageKey),
        manualAnalysisTargets: normalizedTargets,
      }));
    },
    [storageKey],
  );

  const addAnalysisTarget = useCallback(
    (target: AnalysisTarget) => {
      const normalizedTargets = parseAnalysisTargets([target]);
      const nextTarget = normalizedTargets[0];
      if (!nextTarget) {
        return;
      }

      const nextKey = getAnalysisTargetKey(nextTarget);

      setSelectionState((previousState) => {
        const baseState = resolveSelectionState(previousState, storageKey);
        if (
          baseState.manualAnalysisTargets.some(
            (candidate) => getAnalysisTargetKey(candidate) === nextKey,
          )
        ) {
          return baseState;
        }
        return {
          ...baseState,
          manualAnalysisTargets: [
            ...baseState.manualAnalysisTargets,
            nextTarget,
          ],
        };
      });
    },
    [storageKey],
  );

  const removeAnalysisTarget = useCallback(
    (target: AnalysisTarget) => {
      const nextKey = getAnalysisTargetKey(target);
      const pageKey = pageAnalysisTarget
        ? getAnalysisTargetKey(pageAnalysisTarget)
        : null;
      setSelectionState((previousState) => {
        const baseState = resolveSelectionState(previousState, storageKey);
        return {
          ...baseState,
          pageAnalysisTarget:
            pageKey === nextKey ? null : baseState.pageAnalysisTarget,
          manualAnalysisTargets: baseState.manualAnalysisTargets.filter(
            (candidate) => getAnalysisTargetKey(candidate) !== nextKey,
          ),
        };
      });
    },
    [pageAnalysisTarget, storageKey],
  );

  const removeLastAnalysisTarget = useCallback(() => {
    setSelectionState((previousState) => {
      const baseState = resolveSelectionState(previousState, storageKey);
      if (baseState.manualAnalysisTargets.length > 0) {
        return {
          ...baseState,
          manualAnalysisTargets: baseState.manualAnalysisTargets.slice(0, -1),
        };
      }
      if (!baseState.pageAnalysisTarget) {
        return baseState;
      }
      return {
        ...baseState,
        pageAnalysisTarget: null,
      };
    });
  }, [storageKey]);

  const clearAnalysisTargets = useCallback(() => {
    setSelectionState((previousState) => ({
      ...resolveSelectionState(previousState, storageKey),
      manualAnalysisTargets: [],
    }));
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || typeof window === "undefined") {
      return;
    }

    const nextState = {
      version: PERSISTED_SELECTION_STATE_VERSION,
      pageAnalysisTarget,
      manualAnalysisTargets,
    } satisfies PersistedSelectionState;
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(nextState));
    } catch {
      // Storage 例外（quota / security policy）時は UI 操作を優先し、永続化だけを諦める。
    }
  }, [manualAnalysisTargets, pageAnalysisTarget, storageKey]);

  return {
    analysisTargets,
    pageAnalysisTarget,
    manualAnalysisTargets,
    setAnalysisTargets,
    addAnalysisTarget,
    removeAnalysisTarget,
    removeLastAnalysisTarget,
    clearAnalysisTargets,
    setPageAnalysisTarget,
  };
}
